// CommandCenterTopBar — compact contextual nav that replaces the Standup
// topnav while a thread is open.
//
// Layout (flex, single row, wraps under 900px):
//   [Back to Standup]  [brand][channel][order tag] · [thread health]
//   [‹] [›] Thread N of M (bucket label)
//
// Keyboard:
//   J / K — navigate prev / next within the bucket (handled by parent via
//           useBucketNav state-passing — this bar just renders the buttons).
//   Esc   — back to Standup (handled by parent if needed).
//
// Note: this bar replaces the global TopNav for the Center page entirely. We
// keep the MD logo + avatar via a slim header above this. See Center.tsx.

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from '@/components/brand/Tag';
import type { TagVariant } from '@/components/brand/Tag';
import type { ThreadHealthResult } from '@/lib/threadHealth';

const CHANNEL_LABEL: Record<string, string> = {
  gmail: 'Gmail',
  etsy_md: 'Etsy',
  etsy_hk: 'Etsy',
  amazon: 'Amazon',
  shopify_md: 'Shopify',
  shopify_brokerage: 'Brokerage',
};

const CHANNEL_TAG: Record<string, TagVariant> = {
  gmail: 'ch-gmail',
  etsy_md: 'ch-etsy',
  etsy_hk: 'ch-etsy',
  amazon: 'ch-amzn',
  shopify_md: 'ch-shop',
  shopify_brokerage: 'ch-shop',
};

export interface CommandCenterTopBarProps {
  brand: 'md' | 'hk';
  channel: string;
  orderTag?: string | null;
  health: ThreadHealthResult | null;
  bucketLabel: string | null;
  position: { index: number; total: number } | null;
  prevId: string | null;
  nextId: string | null;
  bucketIds: string[];
  bucketKey: string | null;
  /**
   * Render slot for the status pill + snooze popover. Slotted in here (vs
   * hard-coded) so the pill keeps owning its own state wiring in Center.tsx
   * while still riding along inside the frozen topbar per #8.
   */
  statusSlot?: ReactNode;
}

export function CommandCenterTopBar(props: CommandCenterTopBarProps) {
  const {
    brand,
    channel,
    orderTag,
    health,
    bucketLabel,
    position,
    prevId,
    nextId,
    bucketIds,
    bucketKey,
    statusSlot,
  } = props;
  const navigate = useNavigate();

  // "Copy link" for the current thread. Browsers without clipboard API
  // fall back to a text selection trick. Shows a 2-second "Copied" confirm.
  const [copied, setCopied] = useState(false);
  async function copyThreadLink() {
    const href = window.location.href;
    try {
      await navigator.clipboard.writeText(href);
    } catch {
      // Fallback: stash into a hidden textarea and execCommand('copy').
      const ta = document.createElement('textarea');
      ta.value = href;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(ta);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  // J / K navigation. Skip when the user is typing.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t?.isContentEditable ?? false)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'j' || e.key === 'J') {
        if (nextId) {
          e.preventDefault();
          navigate(`/thread/${nextId}`, {
            state: { bucket: bucketKey, ids: bucketIds, label: bucketLabel },
            replace: true,
          });
        }
      } else if (e.key === 'k' || e.key === 'K') {
        if (prevId) {
          e.preventDefault();
          navigate(`/thread/${prevId}`, {
            state: { bucket: bucketKey, ids: bucketIds, label: bucketLabel },
            replace: true,
          });
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevId, nextId, navigate, bucketIds, bucketKey, bucketLabel]);

  return (
    <div className="cc-topbar">
      <button
        type="button"
        className="cc-back"
        onClick={() => navigate('/')}
        aria-label="Back to Standup"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14} aria-hidden>
          <path d="M19 12H5M11 18l-6-6 6-6" />
        </svg>
        Back to Standup
      </button>

      <div className="cc-topbar-center">
        <Tag variant={brand === 'md' ? 'brand-md' : 'brand-hk'}>
          {brand === 'md' ? 'MD' : 'HK'}
        </Tag>
        <Tag variant={CHANNEL_TAG[channel] ?? 'neutral'}>
          {CHANNEL_LABEL[channel] ?? channel}
        </Tag>
        {orderTag ? <Tag variant="neutral">{orderTag}</Tag> : null}
        {health ? (
          <span className={`thread-health ${health.state}`} title={health.label}>
            <span className="dot" aria-hidden />
            {health.label}
          </span>
        ) : null}
        {statusSlot ? <div className="cc-topbar-status">{statusSlot}</div> : null}
      </div>

      <div className="cc-topbar-nav">
        <button
          type="button"
          className="cc-icon-btn"
          aria-label={copied ? 'Thread link copied' : 'Copy link to this thread'}
          title={copied ? 'Copied!' : 'Copy link to this thread'}
          onClick={() => void copyThreadLink()}
        >
          {copied ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path d="M5 12l4 4L19 7" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
              <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07L11 5" />
              <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07L13 19" />
            </svg>
          )}
        </button>
        <button
          type="button"
          className="cc-icon-btn"
          aria-label="Previous thread (K)"
          disabled={!prevId}
          onClick={() => {
            if (prevId) {
              navigate(`/thread/${prevId}`, {
                state: { bucket: bucketKey, ids: bucketIds, label: bucketLabel },
                replace: true,
              });
            }
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
        <button
          type="button"
          className="cc-icon-btn"
          aria-label="Next thread (J)"
          disabled={!nextId}
          onClick={() => {
            if (nextId) {
              navigate(`/thread/${nextId}`, {
                state: { bucket: bucketKey, ids: bucketIds, label: bucketLabel },
                replace: true,
              });
            }
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
        {position && bucketLabel ? (
          <span className="cc-topbar-counter">
            Thread {position.index} of {position.total} · {bucketLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
}
